/**
 * BackupStatusBar — TINY discrete indicator (dot + micro-label) shown ONLY
 * to Supervisor, Superintendent, and Owner roles. Technicians never see it.
 *
 * Josh (Feb 27 refinement): "extremely small and discrete — NOT a full
 * status bar or banner. Just a tiny green dot (or red if failed) with
 * small text like 'Backup OK' tucked into the header or footer."
 *
 * Renders as an inline element — parent decides placement (header slot).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

// The /backup/status endpoint is served by the LOCAL portal backend (see
// `/app/backend/security_routes.py`), NOT the shared prod API — so we bypass
// `lib/api.js` (which points at field-maint.emergent.host) and hit the portal
// origin directly.
const PORTAL_BACKEND = process.env.REACT_APP_BACKEND_URL;
const portalApi = axios.create({ baseURL: PORTAL_BACKEND });

// Roles allowed to see the backup health dot. Technicians / workers get nothing.
const BACKUP_VISIBLE_ROLES = new Set([
  "supervisor", "superintendent", "owner", "site_admin", "site-admin", "control", "admin",
]);

export default function BackupStatusBar() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const role = String(user?.role || "").toLowerCase();
  const canSee = BACKUP_VISIBLE_ROLES.has(role) || Boolean(user?.is_owner);

  useEffect(() => {
    if (!canSee) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await portalApi.get("/api/backup/status");
        if (alive) setStatus(data);
      } catch {
        if (alive) setStatus({ error: true });
      }
    })();
    return () => { alive = false; };
  }, [canSee]);

  if (!canSee || !status || status.error) return null;

  const ok = status?.last_backup?.success && !status.error;
  const dot = ok ? "#4ade80" : "#ef4444";
  const label = ok ? "Backup OK" : "Backup FAIL";
  const ts = status?.last_backup?.timestamp
    ? new Date(status.last_backup.timestamp).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : "";
  const title = ok
    ? `Encrypted backup healthy · last ${ts}`
    : "Backup failed — click to review";

  return (
    <Link
      to="/owner/backups"
      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono-tight opacity-70 hover:opacity-100 transition-opacity"
      style={{ color: "rgba(255,255,255,0.55)", textDecoration: "none" }}
      title={title}
      data-testid="backup-status-bar"
    >
      <span
        style={{
          width: 8, height: 8, borderRadius: 999,
          background: dot,
          boxShadow: `0 0 6px ${dot}`,
          display: "inline-block",
        }}
      />
      {label}
    </Link>
  );
}
